import React, { useState, useCallback, useEffect } from "react";
import { formStyles as s, downloadBtnStyle, DownloadIcon, requiredInput } from "./formStyles";
import SignaturePad from "../components/SignaturePad";
import { displayDateFormat } from "../utils/format";
import { submitResignation } from "../utils/api";

/* ── Constants ── */
const STATUS_OPTIONS = ["Full-time", "Part-time","Casual"];

const REASON_OPTIONS = [
  "Select reason...",
  "Visa expiry.",
  "Change in visa status.",
  "Change in job industry.",
  "No work satisfaction.",
  "Not satisfied with my role.",
  "Co-workers.",
  "Other-refer email.",
];

const ResignationForm = ({ pdfUrl = "/static/pdffilters/Resignation-Letter.pdf", userName = "", userEmail = "" }) => {
  const [form, setForm] = useState({
    name: userName,
    activesite: "",
    statuswork: "Full-time",
    abntfn: "Employee",
    reasonleaving: "Select reason...",
    dateleave: "",
    dateSigned: "",
  });

  const [signature, setSignature] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Prefill name from login
  useEffect(() => {
    if (userName) {
      setForm((p) => ({ ...p, name: userName }));
    }
  }, [userName]);

  const u = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSigSave = useCallback((dataUrl) => {
    setSignature(dataUrl);
  }, []);

  const handleSigClear = useCallback(() => {
    setSignature(null);
  }, []);

  /* ── Validation ── */
  const isValid =
    form.name.trim() &&
    form.activesite.trim() &&
    form.reasonleaving !== "Select reason..." &&
    form.dateleave &&
    form.dateSigned &&
    signature;

  /* ── Download: fill PDF locally with pdf-lib (instant, no server) ── */
  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const { PDFDocument, PDFName } = await import("pdf-lib");
      const pdfBytes = await fetch(pdfUrl).then((r) => {
        if (!r.ok) throw new Error("Could not load PDF template");
        return r.arrayBuffer();
      });
      const pdfDoc = await PDFDocument.load(pdfBytes);
      pdfDoc.setTitle(`Resignation Letter - ${form.name}`);
      const f = pdfDoc.getForm();

      const setText = (name, val) => {
      try { f.getTextField(name).setText(val || ""); } catch (_) {}
        };

      // ── Fill fields ──
      setText("Name", form.name);
      setText("NameTitle", form.name);
      setText("ActiveSite", form.activesite);
      setText("StatusWork", form.statuswork);
      setText("AbnTfn", form.abntfn);
      setText("DateLeave", displayDateFormat(form.dateleave));
      setText("ReasonLeaving", form.reasonleaving);
      setText("DateSigned", displayDateFormat(form.dateSigned));

      // ── Embed signature ──
      if (signature) {
        try {
          const base64 = signature.split(",")[1];
          const sigBytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
          const sigImage = await pdfDoc.embedPng(sigBytes);

          const sigField = f.getTextField("SignEmployee");
          const widgets = sigField.acroField.getWidgets();

          if (widgets.length > 0) {
            const widget = widgets[0];
            const rect = widget.getRectangle();

            const pages = pdfDoc.getPages();
            let targetPage = null;

        for (const page of pages) {
          const annotsRef = page.node.get(PDFName.of("Annots"));
          if (!annotsRef) continue;
          const annots = page.node.context.lookup(annotsRef);
          if (!annots) continue;
          const arr = annots.asArray ? annots.asArray() : [];
          for (const ref of arr) {
            if (ref === widget.ref) { targetPage = page; break; }
          }
          if (targetPage) break;
        }

        if (!targetPage) {
          for (const page of pages) {
            const annotsRef = page.node.get(PDFName.of("Annots"));
            if (!annotsRef) continue;
            const annots = page.node.context.lookup(annotsRef);
            if (!annots) continue;
            const arr = annots.asArray ? annots.asArray() : [];
            for (const ref of arr) {
              const resolved = page.node.context.lookup(ref);
              if (resolved === widget.dict) { targetPage = page; break; }
            }
            if (targetPage) break;
          }
        }

        if (!targetPage) targetPage = pages[pages.length - 1];

            f.removeField(sigField);

            const aspectRatio = sigImage.width / sigImage.height;
            let drawWidth = rect.width;
            let drawHeight = drawWidth / aspectRatio;
            if (drawHeight > rect.height) {
              drawHeight = rect.height;
              drawWidth = drawHeight * aspectRatio;
            }

            const x = rect.x + (rect.width - drawWidth) / 2;
            const y = rect.y + (rect.height - drawHeight) / 2;

            targetPage.drawImage(sigImage, { x, y, width: drawWidth, height: drawHeight });
          }
        } catch (sigErr) {
          console.error("Signature embed error:", sigErr);
          try { f.getTextField("SignEmployee").setText(`Signed by ${form.name}`); } catch (_) {}
        }
      }

      // ── Flatten and download ──
      f.flatten();
      const filled = await pdfDoc.save();
      const blob = new Blob([filled], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Resignation-${form.name || "Form"}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      
      const result = await submitResignation(blob, form.name);

      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.message || "PDF downloaded but failed to upload.");
      }
    } catch (err) {
      console.error("PDF fill error:", err);
      setError(err.message || "Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = () => {
    setForm({
      name: userName,
      activesite: "",
      statuswork: "Full-time",
      abntfn: "Employee",
      reasonleaving: "Select reason...",
      dateleave: "",
      dateSigned: "",
    });
    setSignature(null);
    setError(null);
    setSubmitted(false);
  };

  return (
    <div style={s.wrapper}>
      <style>{s.spinKeyframes}</style>
      <h2 style={s.title}>Cleaners Resignation Letter</h2>
      <p style={s.subtitle}>Resignation from SNL Property Services PTY LTD</p>

      {/* ── Submitted Success View ── */}
      {submitted ? (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{
            width: 80, height: 80, borderRadius: "50%",
            background: "rgba(34,197,94,0.12)", border: "2px solid rgba(34,197,94,0.25)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h2 style={{ ...s.title, fontSize: "1.3rem", marginTop: 20, marginBottom: 8 }}>
            Resignation Submitted
          </h2>
          <p style={{ ...s.subtitle, marginBottom: 24 }}>
            Your resignation letter has been downloaded and recorded successfully.
          </p>
          <button style={s.resetBtn} onClick={handleReset}>Submit Another</button>
        </div>
      ) : (
      <>
      {/* ── Personal & Work Details ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Personal & Work Details</div>
        <div style={s.fieldGrid2}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Full Name *</label>
            <input
              style={requiredInput(form.name)}
              value={form.name}
              onChange={(e) => u("name", e.target.value)}
              placeholder="Enter your full name"
              readOnly={!!userName}
            />
            {userName && (
              <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.35)", marginTop: 2 }}>
                Pre-filled from your account
              </span>
            )}
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Site You Were Working At *</label>
            <input
              style={requiredInput(form.activesite)}
              value={form.activesite}
              onChange={(e) => u("activesite", e.target.value)}
              placeholder="Enter site name"
            />
          </div>
        </div>

        <div style={{ ...s.fieldGrid3, marginTop: 14 }}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Working Status *</label>
            <select style={s.input} value={form.statuswork} onChange={(e) => u("statuswork", e.target.value)}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt} style={s.optionStyle}>{opt}</option>
              ))}
            </select>
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Work Type</label>
            <input
              style={s.input}
              value="Employee"
              readOnly
            />
          </div>
          <div style={s.fieldGroup}>
            <label style={s.label}>Last Working Day *</label>
            <input
              style={requiredInput(form.dateleave)}
              type="date"
              value={form.dateleave}
              onChange={(e) => u("dateleave", e.target.value)}
            />
          </div>
        </div>
      </div>

      <hr style={s.divider} />

      {/* ── Reason for Leaving ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Reason for Resignation</div>
        <div style={s.fieldGroup}>
          <label style={s.label}>Reason for Resigning *</label>
          <select
            style={requiredInput(form.reasonleaving !== "Select reason..." ? form.reasonleaving : "")}
            value={form.reasonleaving}
            onChange={(e) => u("reasonleaving", e.target.value)}
          >
            {REASON_OPTIONS.map((opt) => (
              <option key={opt} value={opt} style={s.optionStyle} disabled={opt === "Select reason..."}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <hr style={s.divider} />

      {/* ── Signature ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Signature</div>
        <div style={s.signatureGrid}>
          <div style={s.signatureBlock}>
            <span style={{
              ...s.signatureLabel,
              color: signature ? "rgba(255,255,255,0.6)" : "rgba(239,68,68,0.7)"
            }}>
              Employee Signature
            </span>
            <SignaturePad label="" width={300} height={120} onSave={handleSigSave} onClear={handleSigClear} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={s.fieldGroup}>
            <label style={s.label}>Date Signed *</label>
            <input
              style={requiredInput(form.dateSigned)}
              type="date"
              value={form.dateSigned}
              onChange={(e) => u("dateSigned", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div style={s.btnRow}>
        <button style={downloadBtnStyle(isValid)} onClick={handleDownload} disabled={!isValid || downloading}>
          {downloading ? (<><span style={s.spinner} /> Generating PDF...</>) : (<><DownloadIcon /> Download Filled PDF</>)}
        </button>
        <button style={s.resetBtn} onClick={handleReset}>Reset</button>
      </div>
      {error && <div style={s.error}>{error}</div>}
      </>
      )}
    </div>
  );
};

export default ResignationForm;